/**
 * @file useConversationList.ts
 * @description Данные списка диалогов (chat-window-design.md §2, §5, §6):
 * вкладка, отбор, поиск, «Показать ещё» по курсору, обновление раз в 15 с.
 *
 * Порядок строк — как отдал сервер (по последнему событию, R4.18); курсор
 * `nextCursor` (`<ISO>|<id>`) передаётся обратно как есть, без разбора. Тихое
 * обновление перечитывает загруженное начало списка (догруженный хвост
 * остаётся, `mergeRefreshedHead`) и не стирает строки при сбое —
 * показывается полоса «Нет связи». Новые диалоги (их не было в прошлом
 * чтении) подсвечиваются 2 с; страница их не двигает сама.
 *
 * Смена вкладки, отбора или поиска начинает новое «поколение» списка:
 * запоздалый ответ «Показать ещё» прежнего отбора отбрасывается и
 * прерывается, а не подмешивается в новый.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { adminChat, type ChatTabCounts, type ChatTab, type StaffConversation } from "../../lib/api-chat";
import { appendPage, mergeRefreshedHead, refreshLimit } from "./conversation-list-merge";

export const LIST_PAGE = 50;
export const LIST_REFRESH_MS = 15_000;
const SEARCH_DEBOUNCE_MS = 300;
const HIGHLIGHT_MS = 2_000;

export interface ConversationListQuery {
  tab: ChatTab;
  unassigned: boolean;
  customerId: string | null;
  search: string;
}

export interface ConversationListData {
  items: StaffConversation[];
  counts: ChatTabCounts | null;
  loading: boolean;
  error: boolean;
  stale: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  /** Диалоги, появившиеся при последнем обновлении (подсветка 2 с). */
  freshIds: ReadonlySet<string>;
  loadMore: () => void;
  reload: () => void;
}

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value.trim());
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value.trim()), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function useConversationList(active: boolean, query: ConversationListQuery, version: number): ConversationListData {
  const search = useDebounced(query.search, SEARCH_DEBOUNCE_MS);
  const [items, setItems] = useState<StaffConversation[]>([]);
  const [counts, setCounts] = useState<ChatTabCounts | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false);
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(new Set());
  const [reloadTick, setReloadTick] = useState(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  /** Поколение отбора: ответы прежних поколений отбрасываются. */
  const generation = useRef(0);
  const moreController = useRef<AbortController | null>(null);
  /** Версия данных, уже учтённая тихим обновлением. */
  const seenVersion = useRef(version);

  const { tab, unassigned, customerId } = query;
  const params = useMemo(
    () => ({ tab, unassigned, customerId: customerId ?? undefined, search: search || undefined }),
    [tab, unassigned, customerId, search],
  );

  // Первая страница при смене вкладки, отбора, поиска.
  useEffect(() => {
    if (!active) return;
    generation.current += 1;
    moreController.current?.abort();
    moreController.current = null;
    setLoadingMore(false);
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    adminChat
      .list({ ...params, limit: LIST_PAGE }, controller.signal)
      .then((page) => {
        setItems(page.items);
        setCounts(page.counts);
        setCursor(page.nextCursor);
        setStale(false);
        setFreshIds(new Set());
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [active, params, reloadTick]);

  // Тихое обновление: по таймеру и после действий (`version`). Сразу —
  // только если версия сменилась: открытие окна первую страницу уже читает.
  useEffect(() => {
    const bumped = version !== seenVersion.current;
    seenVersion.current = version;
    if (!active) return;
    const controller = new AbortController();
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const limit = refreshLimit(itemsRef.current.length, LIST_PAGE);
      adminChat
        .list({ ...params, limit }, controller.signal)
        .then((page) => {
          const known = new Set(itemsRef.current.map((c) => c.id));
          const fresh = page.items.filter((c) => !known.has(c.id)).map((c) => c.id);
          const merged = mergeRefreshedHead(itemsRef.current, cursorRef.current, page, limit);
          setItems(merged.items);
          setCounts(page.counts);
          setCursor(merged.cursor);
          setStale(false);
          if (fresh.length > 0 && known.size > 0) setFreshIds(new Set(fresh));
        })
        .catch(() => {
          if (!controller.signal.aborted) setStale(true);
        });
    };
    if (bumped) refresh();
    const timer = window.setInterval(refresh, LIST_REFRESH_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [active, params, version]);

  // Подсветка новых гаснет за 2 с.
  useEffect(() => {
    if (freshIds.size === 0) return;
    const timer = window.setTimeout(() => setFreshIds(new Set()), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [freshIds]);

  // Окно закрыли или список размонтирован — догрузку прерываем.
  useEffect(() => () => moreController.current?.abort(), []);

  const loadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    const mine = generation.current;
    const controller = new AbortController();
    moreController.current = controller;
    setLoadingMore(true);
    adminChat
      .list({ ...params, cursor, limit: LIST_PAGE }, controller.signal)
      .then((page) => {
        if (mine !== generation.current) return;
        setItems((prev) => appendPage(prev, page.items));
        setCursor(page.nextCursor);
      })
      .catch(() => {
        if (mine === generation.current && !controller.signal.aborted) setStale(true);
      })
      .finally(() => {
        if (mine === generation.current) setLoadingMore(false);
        if (moreController.current === controller) moreController.current = null;
      });
  }, [cursor, loadingMore, params]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  return { items, counts, loading, error, stale, hasMore: cursor !== null, loadingMore, freshIds, loadMore, reload };
}
